import { isNull } from "../TypeUtil";
import ITaskFactory from "./ITaskFactory";

export default class TaskExecutor {

    /**
     * A task executor for executing tasks in parallel, with progress reporting.
     * Tasks can have dependencies on each other, which can be used to make execution partially serial.
     * @param {ITaskFactory} taskFactory - Factory that will instantiate the tasks when they are executed.
     */
    constructor(taskFactory) {
        this._taskFactory = taskFactory;

        this._duration = 0;
        this._progress = 0;
        this._wrappedTasks = [];
        this._status = TaskStatus.NOT_STARTED;
    }

    destroy() {
        this._status = TaskStatus.DESTROYED;
        this._wrappedTasks.length = 0;
        this._callback = null;
        this._progressCallback = null;
    }

    /**
     * 
     * @param {Function} taskClass - Class for task. Typically, a subclass of Task
     * @param {String} id - Reference id for internal use and for specifying dependencies.
     * @param {Number} duration - Estimated duration of task. All tasks should specify a duration in a comparable quantity
     * @param {Array<String>} dependencies - Optional array of ids of tasks on the completeness of which this task depends.
     */
    addTask(taskClass, id, duration = 0, dependencies = []) {
        if (this._status >= TaskStatus.COMPLETED) {
            throw new Error("Can't add tasks after completion.");
        }
        //check duplicate id?
        if (this._wrappedTasks.find(t => id == t.id)) {
            throw new Error(`Duplicate task '${id}'.`);
        }
        //check circular dependencies
        if (this._checkCircularDependencies(id, dependencies)) {
            throw new Error(`Circular dependencies for task '${id}'`);
        }
        //
        this._duration += duration;
        const taskVO = new WrappedTask(taskClass, id, duration, dependencies);
        this._wrappedTasks.push(taskVO);
    }

    /**
     * @param {Function} callback 
     * @param {Function} progressCallback 
     */
    execute(callback, progressCallback) {
        if (this._status > TaskStatus.NOT_STARTED) {
            throw new Error("Already executing.");
        }
        //console.log("TaskExecutor.execute, start execution.");
        this._callback = callback;
        this._progressCallback = progressCallback;
        this._status = TaskStatus.IN_PROGRESS;
        this._progress = 0;
        this._execute();
    }

    get duration() {
        return this._duration;
    }

    get progress() {
        return this._calcProgress();
    }

    _execute() {
        //start all tasks that have no unfulfilled dependencies on other tasks
        this._wrappedTasks.forEach(wrappedTask => {
            if (wrappedTask.status == TaskStatus.NOT_STARTED && this._checkTaskDependenciesCompleted(wrappedTask)) {
                //console.log(`TaskExecutor._execute, task with id '${wrappedTask.id}'`);
                wrappedTask.execute(this._taskFactory, this._onTaskComplete.bind(this), this._onTaskProgress.bind(this));
            }
        });
    }

    /**
     * @callback TaskExecutor~_onTaskComplete
     */
    _onTaskComplete(wrappedTask) {
        if (this._status >= TaskStatus.DESTROYED) {
            return;
        }
        //console.log(`TaskExecutor._onTaskComplete ${wrappedTask.id}`);
        //check new tasks to execute
        this._execute();
        if (this._checkComplete()) {
            this.status = TaskStatus.COMPLETED;
            //remove references to tasks
            this._wrappedTasks.length = 0;
            if (this._callback != null) {
                this._callback();
            }
        }
    }

    _onTaskProgress(wrappedTask, progress) {
        if (this._status >= TaskStatus.DESTROYED) {
            return;
        }
        if (this._progressCallback != null) {
            const total = this._calcProgress();
            this._progressCallback(total / this.duration);
        }
    }

    _calcProgress() {
        let total = 0;
        this._wrappedTasks.forEach(wrappedTask => {
            if (wrappedTask.status >= TaskStatus.IN_PROGRESS) {
                total += wrappedTask.progress * wrappedTask.duration;
            }
        });
        return total;
    }

    _checkComplete() {
        return this._wrappedTasks.every(wrappedTask => wrappedTask.status == TaskStatus.COMPLETED)
    }

    _checkTaskDependenciesCompleted(wrappedTask) {
        return !wrappedTask.dependencies.length
            || wrappedTask.dependencies.every(id => {
                const dep = this._getWrappedTaskById(id);
                //if dependency not found, consider it fulfilled
                return isNull(dep) || dep.status == TaskStatus.COMPLETED;
            });
    }

    _checkCircularDependencies(id, dependencies = []) {
        //traverse dependency tree until we encounter a duplicate id, or until exhaustion
        const circular = this._checkCircularDependenciesRecursive(dependencies, [id]);
        return circular;
    }
    _checkCircularDependenciesRecursive(dependencies, trail) {
        //traverse dependency tree until we encounter a duplicate id, or until exhaustion
        return dependencies.some(id => {
            if (trail.indexOf(id) > -1) {
                return true;
            }
            const wrappedTask = this._getWrappedTaskById(id);
            if (wrappedTask) {
                const trail2 = trail.slice(0);
                trail2.push(id);
                return this._checkCircularDependenciesRecursive(wrappedTask.dependencies, trail2);
            } else {
                return false;
            }
        });
    }

    _getWrappedTaskById(id) {
        return this._wrappedTasks.find(wrappedTask => wrappedTask.id == id);
    }

}

const TaskStatus = {};
TaskStatus.NOT_STARTED = 0;
TaskStatus.IN_PROGRESS = 1;
TaskStatus.COMPLETED = 2;
TaskStatus.DESTROYED = 9;

class WrappedTask {
    /**
     * Wrapper around Task instance to associate id, duration etc and keep track of status and progress
     * @param {Function} taskClass - Class for task. Typically, a subclass of Task
     * @param {String} id - Reference id for internal use and for specifying dependencies.
     * @param {Number} duration - Estimated duration of task. All tasks should specify a duration in a comparable quantity
     * @param {Array<String>} dependencies - Optional array of ids of tasks on the completeness of which this task depends.
     */
    constructor(taskClass, id, duration, dependencies) {
        this.taskClass = taskClass;
        this.id = id;
        this.duration = duration;
        this.dependencies = dependencies;

        this.status = TaskStatus.NOT_STARTED;
        this.progress = 0;
    }

    execute(taskFactory, callback, progressCallback) {
        if (this.status > TaskStatus.NOT_STARTED) {
            throw new Error("Already executing.");
        }
        //instantiate task
        const task = taskFactory.instantiateTask(this.taskClass);

        this.status = TaskStatus.IN_PROGRESS;
        const wrappedCallback = (task) => {
            this.status = TaskStatus.COMPLETED;
            this.progress = 1;
            progressCallback(this, 1);
            callback(this);
        }
        const wrappedProgressCallback = (task, p) => {
            if (isNaN(parseFloat(p)) || p < 0 || p > 1) {
                throw new Error("Task progress must be number in range 0..1: " + p);
            }
            this.progress = p;
            progressCallback(this, p);
        }
        task.execute(wrappedCallback, wrappedProgressCallback);
    }
}